/**
 * buildCompareArrows.js
 *
 * Pure utility — no React, no state.
 *
 * Two-step usage:
 *
 *   import { precomputeCentroids, buildCompareArrows } from "./buildCompareArrows";
 *
 *   // 1. Call once when geo changes (e.g. in loadGeo / componentDidUpdate):
 *   const centroids = precomputeCentroids(geoData.features);
 *   this.setState({ centroids });
 *
 *   // 2. Call on every data / wave change (cheap — no geometry work):
 *   const arrows = buildCompareArrows({
 *     centroidMap: this.state.centroids,
 *     deltaRows,
 *     valueKey: "nat_pred_n",
 *   });
 */

// ── Shoelace area of a single ring (absolute value) ─────────────────────────
// Cheap: just addition + multiplication on the coordinate pairs you already have.
function ringArea(ring) {
  let area = 0;
  for (let i = 0, len = ring.length, j = len - 1; i < len; j = i++) {
    area += (ring[j][0] - ring[i][0]) * (ring[j][1] + ring[i][1]);
  }
  return Math.abs(area / 2);
}

// ── Centroid of a single ring ────────────────────────────────────────────────
function ringCentroid(ring) {
  let x = 0, y = 0;
  for (const [lng, lat] of ring) {
    x += lng;
    y += lat;
  }
  const n = ring.length;
  return n ? [x / n, y / n] : null;
}

// ── Precompute centroids — call once per geo change ─────────────────────────
/**
 * Groups features by area_id, finds the single largest polygon across all
 * features sharing that id, and returns a Map<area_id, [lng, lat]>.
 *
 * @param {Array} geoFeatures — array of GeoJSON features with properties.area_id
 * @returns {Map<string, [number, number]>}
 */
export function precomputeCentroids(geoFeatures) {
  if (!geoFeatures?.length) return new Map();

  // Group features by area_id
  const grouped = new Map();
  for (const f of geoFeatures) {
    const id = String(f.properties?.area_id ?? "");
    if (!id) continue;
    if (!grouped.has(id)) grouped.set(id, []);
    grouped.get(id).push(f);
  }

  // For each area, find the largest polygon across all its features
  const result = new Map();
  for (const [id, feats] of grouped) {
    let bestCentroid = null;
    let bestArea = -1;

    for (const f of feats) {
      const geom = f?.geometry;
      if (!geom) continue;

      let rings;
      if (geom.type === "Polygon") {
        rings = [geom.coordinates[0]];
      } else if (geom.type === "MultiPolygon") {
        rings = geom.coordinates.map((poly) => poly[0]);
      } else {
        continue;
      }

      for (const ring of rings) {
        if (!ring?.length) continue;
        const a = ringArea(ring);
        if (a > bestArea) {
          bestArea = a;
          bestCentroid = ringCentroid(ring);
        }
      }
    }

    if (bestCentroid) result.set(id, bestCentroid);
  }

  return result;
}

// ── Density scale — shrinks arrows when there are many areas ────────────────
function getDensityScale(areaCount) {
  if (areaCount > 1200) return 0.35;
  if (areaCount > 800)  return 0.45;
  if (areaCount > 500)  return 0.60;
  if (areaCount > 300)  return 0.75;
  return 1;
}

// ── Main builder — fast, no geometry work ───────────────────────────────────
/**
 * @param {Object}   opts
 * @param {Map}      opts.centroidMap — from precomputeCentroids()
 * @param {Array}    opts.deltaRows   — one row per area with delta values
 * @param {string}   [opts.valueKey]  — column name that holds the delta
 * @param {Function} [opts.valueFn]   — custom accessor (overrides valueKey)
 * @param {string}   [opts.posColor]  — hex for positive arrows  (default "#15803d")
 * @param {string}   [opts.negColor]  — hex for negative arrows  (default "#b91c1c")
 * @param {number}   [opts.posAngle]  — rotation for positive    (default 45 = NE)
 * @param {number}   [opts.negAngle]  — rotation for negative    (default 225 = SW)
 * @returns {Object} GeoJSON FeatureCollection of Point features
 */
export function buildCompareArrows({
  centroidMap = new Map(),
  deltaRows = [],
  valueKey = null,
  valueFn = null,
  posColor = "#15803d",
  negColor = "#b91c1c",
  posAngle = 45,
  negAngle = 225,
} = {}) {
  if (!centroidMap.size || !deltaRows.length) {
    return { type: "FeatureCollection", features: [] };
  }

  const getValue = valueFn
    ? valueFn
    : valueKey
      ? (row) => Number(row?.[valueKey])
      : () => NaN;

  const allValues = [];
  const featureData = [];

  for (const row of deltaRows) {
    const id = String(row.area_id ?? "");
    const centroid = centroidMap.get(id);
    if (!centroid) continue;

    const v = getValue(row);
    if (!Number.isFinite(v) || v === 0) continue;

    allValues.push(v);
    featureData.push({ centroid, v });
  }

  if (!allValues.length) {
    return { type: "FeatureCollection", features: [] };
  }

  const maxAbs = Math.max(...allValues.map((v) => Math.abs(v)));
  const densityScale = getDensityScale(centroidMap.size);

  const sizeScale = (v) => {
    const t = Math.sqrt(Math.min(1, Math.abs(v) / maxAbs));
    return (0.3 + t * 1.5) * densityScale;
  };

  const opacityScale = (v) => {
    const t = Math.min(1, Math.abs(v) / maxAbs);
    return 0.85 + t * 0.15;   // range: 0.85 → 1.0, always punchy
  };

  const features = featureData.map(({ centroid, v }) => ({
    type: "Feature",
    geometry: { type: "Point", coordinates: centroid },
    properties: {
      angle:   v >= 0 ? posAngle : negAngle,
      size:    sizeScale(v),
      opacity: opacityScale(v),
      color:   v >= 0 ? posColor : negColor,
      delta:   v,
    },
  }));

  return { type: "FeatureCollection", features };
}

// ── Arrow image registration (call once per map instance) ────────────────────
/**
 * Registers the SDF arrow icon with a Mapbox/MapLibre map instance.
 * Safe to call multiple times — skips if already registered.
 *
 * @param {Object} map — the map instance from mapRef.current.getMap()
 * @param {string} [imageName="compare-arrow"]
 */
export function registerArrowImage(map, imageName = "compare-arrow") {
  if (!map || map.hasImage?.(imageName)) return;

 const svg = `
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="32" viewBox="0 0 20 32">
    <path fill="white" d="M10 1 L18 13 L12 11 L12 31 L8 31 L8 11 L2 13 Z"/>
  </svg>
`;
  const img = new Image(20, 32);
  img.onload = () => {
    if (!map.hasImage(imageName)) {
      map.addImage(imageName, img, { sdf: true });
    }
  };
  img.src = "data:image/svg+xml;utf8," + encodeURIComponent(svg);
}